import { useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/App.css";
import HomePage from "@/pages/HomePage";
// ProductPage is eager: /product ships prerendered HTML and hydrates —
// a lazy route boundary would suspend mid-hydration and force a costly
// client re-render (React error #418).
import ProductPage from "@/pages/ProductPage";
import { LEGAL_TERMS, PRIVACY_POLICY } from "@/config/store";

// Route-level code splitting: the landing + product pages (entry +
// LCP-critical) stay in the main bundle; waitlist/surprise/legal load on
// demand.
const ReturnsExchangesPage = lazy(() => import("@/pages/ReturnsExchangesPage"));
const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"));
const WaitlistSurprisePage = lazy(() => import("@/pages/WaitlistSurprisePage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));

export default function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    // Lenis smooth-scroll is desktop-only: on iOS / in-app browsers it can
    // block native touch scrolling, so touch devices keep native scroll.
    // Dynamically imported so it never lands in the initial JS bundle.
    const finePointer = window.matchMedia("(pointer: fine)").matches;
    if (!finePointer) return;
    let lenis = null;
    let raf;
    import("lenis").then(({ default: Lenis }) => {
      lenis = new Lenis({ duration: 1.1, smoothWheel: true });
      lenisRef.current = lenis;
      const loop = (t) => {
        lenis.raf(t);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="App bg-ivory text-ink font-body">
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/returns-exchanges" element={<ReturnsExchangesPage />} />
          <Route path="/early-access" element={<WaitlistPage />} />
          <Route path="/waitlist-surprise" element={<WaitlistSurprisePage />} />
          <Route
            path="/privacy"
            element={
              <LegalPage
                title="Privacy Policy | POSSO"
                heading="Privacy."
                updated={PRIVACY_POLICY.updated}
                intro={PRIVACY_POLICY.intro}
                sections={PRIVACY_POLICY.sections}
                path="/privacy"
                description="How POSSO collects, stores and uses your information — including Microsoft Clarity, Meta and Shopify — and how to unsubscribe or request deletion."
              />
            }
          />
          <Route
            path="/terms"
            element={
              <LegalPage
                title="Terms of Sale | POSSO"
                heading="Terms."
                body={LEGAL_TERMS}
                path="/terms"
                description="POSSO terms of sale for preorders — charging, cancellation and dispatch."
              />
            }
          />
          <Route path="*" element={<HomePage />} />
        </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  );
}
